import { inject, provide, type InjectionKey } from 'vue';
import type { MeridiaDrawerPassThrough } from 'src/design_system/components/drawer/types';

/** Renderless scope wrapper for inline regions / `v-for` lists (see TestIdScope.vue). */
export { default as TestIdScope } from './TestIdScope.vue';

/**
 * Central `data-testid` layer.
 *
 * Components declare only a LOCAL segment; the full hierarchical id is composed
 * automatically from ancestor scopes via provide/inject, so a component keeps a
 * stable id no matter where it is mounted (refactor-resilient). With no ancestor
 * scope, `tid('x')` is just `'x'` — scoping is opt-in, only where you need
 * uniqueness.
 *
 * - Leaf element:      `const tid = useTestId(); :data-testid="tid('save')"`
 * - Page/section/row:  `const tid = provideTestIdScope('billing')`
 * - Inline / lists:    `<TestIdScope segment="billing" v-slot="{ tid }">`
 *
 * provide/inject follows the COMPONENT tree, not the DOM, so teleported overlays
 * (dialogs, menus) keep their logical scope. Select by the full flat id — never
 * by DOM-descendant chains.
 */

/**
 * Composes a local segment into a full, scoped `data-testid` value.
 * Returns `undefined` in production deploys (production build with
 * `VITE_ENV === 'prod'`), so Vue omits the attribute entirely — both on direct
 * `:data-testid` binds and inside `pt` objects. Stage/dev/test keep the ids
 * for QA e2e.
 */
export type TestIdFn = (local: string) => string | undefined;

const SCOPE_KEY: InjectionKey<string> = Symbol('testid-scope');

/** Normalize one path segment to the kebab-case `data-testid` convention. */
export function slugifyTestId(segment: string): string {
  return segment
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function joinTestId(scope: string, local: string): string | undefined {
  // Strip only in a real production deploy: a production BUILD (`yarn dev` and
  // vitest keep ids regardless of the local .env) of the prod ENVIRONMENT
  // (stage deploys also run `vite build`, but QA e2e needs the ids there).
  // Read per call, not at module load, so tests can stub the env.
  if (
    import.meta.env.MODE === 'production' &&
    import.meta.env.VITE_ENV === 'prod'
  ) {
    return undefined;
  }
  return [scope, slugifyTestId(local)].filter(Boolean).join('-');
}

/**
 * Resolver bound to the current scope, without extending it. Use on leaf
 * elements. Must be called in `setup` (it injects).
 */
export function useTestId(): TestIdFn {
  const scope = inject(SCOPE_KEY, '');
  return (local) => joinTestId(scope, local);
}

/**
 * Extend the scope for all descendants by one segment and return a resolver
 * bound to the NEW scope. Call once in a page/section/row `setup`.
 *
 * The returned resolver is needed because a resolver captured by `useTestId()`
 * in the same setup is bound to the parent scope, not this newly-provided one.
 */
export function provideTestIdScope(segment: string): TestIdFn {
  const parent = inject(SCOPE_KEY, '');
  const scope = [parent, slugifyTestId(segment)].filter(Boolean).join('-');
  provide(SCOPE_KEY, scope);
  return (local) => joinTestId(scope, local);
}

/**
 * `pt` giving a MeridiaDrawer's chrome the ids the old drawer wrapper emitted itself
 * (`panel`, `title`, `close`; plus `subtitle`), so existing QA selectors keep working.
 * Pass the `tid` of the scope the drawer should own.
 */
export function drawerChromeTestIds(tid: TestIdFn): MeridiaDrawerPassThrough {
  return {
    root: { 'data-testid': tid('panel') },
    title: { 'data-testid': tid('title') },
    subtitle: { 'data-testid': tid('subtitle') },
    closeButton: { 'data-testid': tid('close') },
  };
}
